import { defineComponent as _defineComponent } from 'vue'
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import {
	SnowplowTitleContextGraphql,
	SnowplowClickoutContextGraphql,
	type SnowplowContext,
} from '@/helpers/tracking/providers/snowplow-contexts';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import SingleRankingDisplay from '@/components/streaming-charts/SingleRankingDisplay.vue';
import WatchNowButton from '@/components/buttons/WatchNowButton.vue';
import Top10TitleSwiper from './Top10TitleSwiper.vue';
import { getSelectedTab } from './helper';
import { computed, ref, watch } from 'vue';
import { useMounted } from '@vueuse/core';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useMediaQuery } from '@vueuse/core';
import Provider from '@/interfaces/provider';


export default /*#__PURE__*/_defineComponent({
  __name: 'Top10HorizontalTitleListGraphql',
  props: {
    provider: null,
    titles: { default: () => [] },
    isBlocking: { type: Boolean },
    preventDefault: { type: Boolean },
    showQuickActions: { type: Boolean },
    hasMoreLikeThis: { type: Boolean },
    showSeenList: { type: Boolean },
    showMobileTitle: { type: Boolean },
    titleText: { default: '' },
    showPosition: { type: Boolean },
    additionalContexts: { default: () => [] }
  },
  emits: ["scrollbarClicked", "onTitleClick"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'scrollbarClicked', payload: { direction: 'left' | 'right'; position: number }): void;
	(e: 'onTitleClick', payload: { index: number; title: TitleDetail }): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { provider: Provider, titles: TitleDetail[], isBlocking?: boolean, preventDefault?: boolean, showQuickActions?: boolean, hasMoreLikeThis?: boolean, showSeenList?: boolean, showMobileTitle?: boolean, titleText: string, showPosition?: boolean, additionalContexts: SnowplowContext[] };





const { isLoggedIn } = storeToRefs(useUserStore());
const languageStore = storeToRefs(useLanguageStore());
const routingStore = useRoutingStore();

const isMounted = useMounted();
const isLoading = ref(false);

const isMobileViewport = useMediaQuery('(max-width: 512px)');
const isMobileView = computed(() => isMounted.value && isMobileViewport.value);

const isFrench = computed(() => languageStore.language.value === 'fr');

// For Apple TV Plus we override the watch now button position and icon
const isAppleTvPlus = computed(() => props.provider.shortName === 'atp');
const appleTvPlusIcon = `/${ASSETS_DIR}/img/icons/ATP_white_logo_sm.svg`;

const titlesContainerComp = computed(() => {
	if (isMobileView.value) {
		return {
			cmp: Top10TitleSwiper,
			props: {
				titleCount: props.titles.length,
			},
			methods: { slideChange },
		};
	}

	return {
		cmp: HiddenHorizontalScrollbar,
		props: {
			additionalContexts: props.additionalContexts,
		},
	};
});

watch(
	() => props.titles,
	() => {
		isLoading.value = true;

		setTimeout(() => {
			isLoading.value = false;
		}, 200);
	}
);

function slideChange(payload: { direction: 'left' | 'right'; position: number }) {
	emit('scrollbarClicked', payload);
}

function handleTitleClick(index: number, title: TitleDetail) {
	emit('onTitleClick', { index, title });
}

function getTitleName(title: TitleDetail) {
	if (title.__typename === 'Season') {
		return title?.show?.content?.title;
	}

	return title.content.title;
}

function handleWatchNowClick(titleNode: TitleDetail) {
	const selectedTab = getSelectedTab(routingStore.activeRoute?.name as string);

	const contexts: SnowplowContext[] = [new SnowplowTitleContextGraphql(titleNode.objectId, titleNode.objectType)];
	if (titleNode.watchNowOffer) {
		contexts.push(SnowplowClickoutContextGraphql.fromProviderOffer(titleNode.watchNowOffer));
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'watchnow_clicked',
			label: 'top-10-titles',
			property: selectedTab,
		},
		contexts
	);
}

return { __sfc: true,props, emit, isLoggedIn, languageStore, routingStore, isMounted, isLoading, isMobileViewport, isMobileView, isFrench, isAppleTvPlus, appleTvPlusIcon, titlesContainerComp, slideChange, handleTitleClick, getTitleName, handleWatchNowClick, PosterGraphql, TitleDetailLink, SingleRankingDisplay, WatchNowButton }
}

})